<template>
    <DModal
        :title="__('customers.invoices.installments.edit_installments_btn')"
        :show="showModal"
        is-big
        @close="closeModal"
    >
        <DRow>
            <DCol class="p-5">
                <DFormRow :label="__('customers.invoices.installments.form.invoice_period')">
                    <span>{{ formatDate(invoiceFromDate) }} - {{ formatDate(invoiceToDate) }}</span>
                </DFormRow>
                <DRow
                    class="py-3"
                    style="font-weight: bold"
                >
                    <DCol col="2">
                        {{ __('customers.invoices.installments.form.number') }}
                    </DCol>
                    <DCol col="4">
                        {{ __('customers.invoices.installments.form.date') }}
                    </DCol>
                    <DCol col="3">
                        {{ __('customers.invoices.installments.form.amount') }}
                    </DCol>
                    <DCol col="3">
                        {{ __('customers.invoices.installments.form.status') }}
                    </DCol>
                </DRow>
                <DRow
                    v-for="(installment, index) in installments"
                    :key="index"
                    :class="{ 'py-2': true, 'text-muted': !editableInstallment(installment) }"
                >
                    <DCol
                        col="2"
                        class="py-2"
                    >
                        {{ installment.number }}
                    </DCol>
                    <DCol col="4">
                        <DDateFormatInput
                            :key="componentKey"
                            v-model="installment.due_date"
                            format="YYYY-MM-DD"
                            type="date"
                            :is-valid="isFormFieldValid('start_date_new')"
                            :disabled="!editableInstallment(installment)"
                        />
                        <span
                            v-if="!isDateWithinRange(installment.due_date)"
                            class="text-color-red"
                        >
                            {{ __('customers.invoices.installments.form.warning') }}
                        </span>
                    </DCol>
                    <DCol
                        col="3"
                        class="py-2"
                    >
                        {{ installment.amount }}
                    </DCol>
                    <DCol
                        col="3"
                        class="py-2"
                    >
                        {{ !editableInstallment(installment) ? installment.status : '' }}
                    </DCol>
                </DRow>
                <DRow class="pt-5">
                    <DCol col="8">
                        <DFormTextarea
                            v-if="saveBtnEnabled"
                            id="activity_log_comment"
                            v-model="form.activity_log_comment"
                            class="d-inline-block"
                            :placeholder="__('messages.change_reason')"
                            add-input-classes="bg-light"
                            :is-valid="!changeReasonError"
                        />
                    </DCol>
                    <DCol col="4">
                        <DButton
                            v-if="saveBtnEnabled"
                            class="mt-4"
                            style="float: right"
                            :label="__('customers.invoices.installments.form.save_btn')"
                            @click="saveInstallmentPlan"
                        />
                    </DCol>
                </DRow>
            </DCol>
        </DRow>
    </DModal>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { route } from '~@/Composables/route'
import { useForm } from '@inertiajs/vue3'
import dateFormat from '~@/Mixins/dateFormat'
import { isEqual, cloneDeep, map } from 'lodash'

const emit = defineEmits(['update:showModal'])

const props = defineProps({
    showModal: {
        type: Boolean,
        default: false
    },
    invoice: {
        type: Object,
        default: () => ({})
    }
})

const showModal = ref(props.showModal)

const installments = ref(cloneDeep(props.invoice.installments).map(installment => ({
    ...installment,
    due_date: dateFormat.methods.setDateFormatEuropeBerlin(installment.due_date)
})))
const componentKey = ref(0)
const changeReasonError = ref(false)

const form = useForm({
    installments: [],
    activity_log_comment: ''
})

// Define a function to force a rerender by incrementing the component key
const forceRerender = () => {
    componentKey.value += 1
}

const formatDate = (date) => dateFormat.methods.setDateFormatEuropeBerlin(date)

const invoiceFromDate = computed(() => props.invoice.date)

const invoiceToDate = computed(() => {
    if (props.invoice.items.length === 0) return ''

    const latestInvoiceDate = props.invoice.items.reduce(
        (latest, item) => (new Date(item.to_date) > new Date(latest) ? item.to_date : latest),
        props.invoice.items[0].to_date
    )

    return new Date(latestInvoiceDate)
})

const editableInstallment = (installment) => {
    return (
        installment.status === 'unpaid' &&
        (installment.open_items !== null
            ? installment.amount === installment.open_items
            : installment.amount === installment.amount_to_pay)
    )
}

const saveBtnEnabled = computed(() => {
    if (form.installments.length === 0) {
        return false
    }
    const formattedFormInstallments = map(form.installments, installment => ({
        ...installment,
        due_date: dateFormat.methods.setDateFormatEuropeBerlin(installment.due_date)
    }))

    const formattedInvoiceInstallments = map(props.invoice.installments, installment => ({
        ...installment,
        due_date: dateFormat.methods.setDateFormatEuropeBerlin(installment.due_date)
    }))

    return !isEqual(formattedFormInstallments, formattedInvoiceInstallments)
})

const saveInstallmentPlan = () => {
    form.installments = installments.value

    if (form.activity_log_comment.length < 5) {
        changeReasonError.value = true
        return
    }

    form.post(
        route('customers.customers.invoices.update-payment-plans', {
            customer: parseInt(props.invoice.customer_id),
            invoice: parseInt(props.invoice.id)
        }), {
            onSuccess: () => {
                closeModal()
                forceRerender()
            }
        }
    )
}

const closeModal = () => {
    form.installments = []
    installments.value = props.invoice.installments.map(installment => ({
        ...installment,
        due_date: dateFormat.methods.setDateFormatEuropeBerlin(installment.due_date)
    }))
    form.activity_log_comment = ''
    changeReasonError.value = false
    emit('update:showModal', false)
}

function isDateWithinRange (date) {
    const installmentDate = new Date(dateFormat.methods.setDateFormatEuropeBerlin(date))
    const fromDate = new Date(dateFormat.methods.setDateFormatEuropeBerlin(invoiceFromDate.value))
    const toDate = new Date(dateFormat.methods.setDateFormatEuropeBerlin(invoiceToDate.value))

    return installmentDate >= fromDate && installmentDate <= toDate
}

watch(() => props.showModal, (newValue) => {
    showModal.value = newValue
})

watch(installments, (newInstallments) => {
    form.installments = newInstallments
}, { deep: true })
</script>

<style>
.text-color-red {
    color: #FF0000FF;
}
</style>
